import {
    ArrowDownwardRounded as ArrowDownwardRoundedIcon,
    ArrowOutwardRounded as ArrowOutwardRoundedIcon,
    ArrowUpwardRounded as ArrowUpwardRoundedIcon,
    AttachMoney,
    Block as BlockIcon,
    BusinessCenter,
    ExpandMoreRounded as ExpandMoreRoundedIcon,
    FolderSharedOutlined as FolderSharedOutlinedIcon,
    FormatListBulleted as FormatListBulletedIcon,
    InfoOutlined as InfoOutlinedIcon,
    MoreVert as MoreVertIcon,
    Person,
} from 'components/icons';
import ExclamationMarkIcon from 'assets/images/icons/ExclamationMarkIcon.svg?react';
import ViewCozyIcon from 'assets/images/icons/ViewCozy.svg?react';
import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';

import type { DataTableColumnDef } from 'components/tables/data-table';

import Chip from 'components/@extended/Chip';
import SegmentChip from 'components/@extended/SegmentChip';
import Loader from 'components/Loader';
import RelationshipPartnersAndManagers from 'components/misc/RelationshipPartnersAndManagers';
import KebabMenu, { MenuItem as KebabMenuItem } from 'components/navigation/KebabMenu';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { ViewSwitch, ViewSwitchItem } from 'components/ViewSwitch';
import { Separator } from 'components/ui/separator';
import { Tooltip } from 'components/ui/tooltip';
import { columnWidthVar } from 'components/tables/data-table/cssVars';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import NestedTable, { NestedColumn } from 'components/tables/list-table/NestedTable';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import TooltipHeader from 'components/tables/TooltipHeader';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useHasSeveralActiveOffices } from 'hooks/useHasSeveralActiveOffices';
import { useViewParam } from 'hooks/useViewParam';

import { calculateTimeSince, TimeSinceCell } from 'pages/contacts/util';
import GroupIcon from 'pages/groups/GroupIcon';
import { useLazyGetPortfolioPartiesWithFiltersQuery } from 'store/api/portfolio';
import { VerificationStatusType } from 'types/compliance';
import { PartyType, RelationshipTypeCode } from 'types/contacts';
import { PaginationProps } from 'types/paginationAndFilter';
import {
    buildPortfolioScopeParams,
    PORTFOLIO_VIEW_MODES,
    PORTFOLIO_SCOPE_PARAM_KEYS,
    PortfolioListItem,
    PortfolioRowType,
    PortfolioScopeState,
    PortfolioViewMode,
} from 'types/portfolio';
import { errorAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useLastEffortType } from 'hooks/useLastEffortType';
import { isLastEffortStale } from 'utils/lastEffortType';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { VIEW_PARAM_KEY } from 'utils/viewParam';
import { buildPortfolioMainFilters } from './portfolioFilters';
import {
    ACTIVE_CLIENT_LOOKBACK_MONTHS,
    buildOwnershipFilterLabel,
    buildOwnershipFilterTooltip,
    buildOwnershipLineMessage,
    buildPortfolioLegendLabel,
    INITIAL_SEARCH_PARAMS,
} from './portfolioScope';
import PortfolioScopeSelector from './PortfolioScopeSelector';
import { cn } from 'utils/cn';
import { Setting } from 'types/back-office-settings';
import { useSettings } from 'utils/useSettings';

// The new design keeps a label whole, never broken mid-word, and wraps its row of chips instead.
const SERVICE_LEVEL_CLASS = 'text-subtitle2 text-grey-600 flex items-center font-semibold new:whitespace-nowrap';
const CHIP_ROW_CLASS = 'new:flex-wrap';

const USED_CLASSIFIERS = [
    'RELATIONSHIP_TYPE',
    'SEGMENT_TYPE',
    'SERVICE_LEVEL_TYPE',
    'BUSINESS_AREA_TYPE',
    'COMPLIANCE_VERIFICATION_STATUS_TYPE',
    'GROUP_PARTNERSHIP_TYPE',
    // Filter-panel options
    'COUNTRY',
    'KEYWORD_TYPE',
    'MATTER_STATE_TYPE',
];

// BE sends a mix of LocalDate ('yyyy-MM-dd') and OffsetDateTime ('yyyy-MM-ddTHH:mm:ssZ').
// Slicing the first 10 chars yields the intended calendar date for both without routing
// through `new Date(...)` — which would shift dates across timezones for west-of-UTC users.
const formatDate = (d: string | null) => (d ? d.substring(0, 10) : '—');

// A group row the backend kept on the page only because a contact under it matched the filter
// panel — the group itself did not. `true` (matched on its own columns) and `null` (the request
// carried no panel clause at all) are ordinary rows. `false` is taken at face value: it also
// covers a panel clause no group can be tested on, and such a row is context either way.
const isContextOnlyGroupRow = (row: PortfolioListItem) =>
    row.rowType === PortfolioRowType.GROUP && row.matchesFilter === false;

// Greys a figure cell on a context-only row, descendants included. The overdue amount and the YoY
// arrow each set a colour of their own, and a red figure on a row the page is explicitly marking
// "not a result" contradicts the signal the grey is there to give — so the grey wins over both.
// `**:` reaches every descendant, and it is important so it outranks the descendants' own colour
// utilities and the table's unlayered cell-link rule.
const greyWhenContextOnly = (row: PortfolioListItem, cell: React.ReactNode) =>
    isContextOnlyGroupRow(row) ? <div className="text-grey-600 **:text-grey-600!">{cell}</div> : cell;

// Orange side-border legend. The label adapts to the active scope — "My clients",
// "[Name]'s clients", "Selected people's clients" or "Team's clients"; the parent passes null in
// firm scope, where the grey-row key below is then the only line the block carries. Single Tooltip
// wrapping both the label and the i-icon so hovering anywhere triggers it.
const PortfolioOwnershipLegend = React.memo(
    ({ label, showContextRowKey }: { label: string | null; showContextRowKey: boolean }) => {
        return (
            <div className="flex flex-col gap-0.5">
                {label ? (
                    <Tooltip content={<FormattedMessage id="portfolio.legend.tooltip" />}>
                        <div className="border-secondary flex cursor-default items-center gap-1.5 border-l-4 py-0.5 pl-2">
                            <p className="text-body2 text-fg">{label}</p>
                            <InfoOutlinedIcon className="text-grey-400 h-5 w-auto" />
                        </div>
                    </Tooltip>
                ) : null}
                {/* Key for the grey rows, carried only while the fetched page actually holds one.
                 *  Plain text on purpose: the per-row hover carries the explanation. */}
                {showContextRowKey ? (
                    <div className="text-grey-600 pl-2 text-sm">
                        <FormattedMessage id="portfolio.legend.context-row" />
                    </div>
                ) : null}
            </div>
        );
    }
);
PortfolioOwnershipLegend.displayName = 'PortfolioOwnershipLegend';

const usePortfolioRowActions = () => {
    const navigateWithReturn = useNavigateWithReturnState();

    return useCallback(
        (partyId: number): KebabMenuItem[] => [
            {
                title: t('portfolio.actions.add-bd-effort'),
                handler: () => navigateWithReturn(`/contact/${partyId}/effort/new`),
            },
            {
                title: t('portfolio.actions.add-timecard'),
                // Prefilter the timecard form's Case/Client picker to this contact's cases.
                handler: () => navigateWithReturn(`/contact/${partyId}/timecards/new`),
            },
            {
                title: t('portfolio.actions.add-task'),
                handler: () => navigateWithReturn(`/contact/${partyId}/task/new`),
            },
        ],
        [navigateWithReturn]
    );
};

// Module-level so the reference stays stable — the filter hook keys a callback on it.
const PORTFOLIO_PRESERVED_PARAM_KEYS = [...PORTFOLIO_SCOPE_PARAM_KEYS, VIEW_PARAM_KEY] as const;

interface MyPortfolioTableProps {
    scope: PortfolioScopeState;
    onScopeChange: (next: PortfolioScopeState) => void;
    canUseScopeSelector: boolean;
}

const getPortfolioRowHref = (row: PortfolioListItem) =>
    row.rowType === PortfolioRowType.GROUP ? `/group/${row.id}` : `/contact/${row.id}`;

const MyPortfolioTable = ({ scope, onScopeChange, canUseScopeSelector }: MyPortfolioTableProps) => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const { isSettingEnabled } = useSettings();
    const lastEffortType = useLastEffortType();
    const hasLastEffortType = !!lastEffortType;
    const lastEffortTypeName = lastEffortType?.name ?? '';
    const lastEffortTypeLabel = lastEffortType?.label ?? '';
    const hasSeveralActiveOffices = useHasSeveralActiveOffices();
    const isNonVerifiedIconEnabled = isSettingEnabled(Setting.NON_VERIFIED_CLIENT_ICON);
    const dateFormat = useEffectiveDateFormat();
    const isNewDesign = useIsNewDesign();
    // Read above the table, which `key={viewMode}` remounts on every switch.
    const [viewMode, setViewMode] = useViewParam('my-portfolio', PORTFOLIO_VIEW_MODES, PortfolioViewMode.GROUP);
    const [fetchData, { data, isFetching, error }] = useLazyGetPortfolioPartiesWithFiltersQuery();
    const buildRowActions = usePortfolioRowActions();

    const fetchDataWithViewMode = useCallback(
        (params: PaginationProps) =>
            fetchData({
                ...params,
                // Scope (People / Team / Firm) rides alongside viewMode as a query param.
                // The table is keyed by scope in the parent, so this closure always sees the
                // active scope on the (re)mount's initial fetch.
                extraParams: { ...params.extraParams, viewMode, ...buildPortfolioScopeParams(scope) },
            }),
        [fetchData, viewMode, scope]
    );

    // Only alert once per distinct error occurrence (strict-mode re-mounts fire the effect twice).
    const lastNotifiedError = useRef<unknown>(null);
    useEffect(() => {
        if (error && lastNotifiedError.current !== error) {
            lastNotifiedError.current = error;
            errorAlert(t('portfolio.error-loading-list'));
        }
    }, [error]);

    // Cell renderers shared between the Tier 1 DataTable columns and the Tier 2
    // NestedTable columns. Keeping the rendering logic in one place means group rows
    // and member rows always look identical apart from the per-tier framing.
    const renderNameCell = useCallback(
        (row: PortfolioListItem, isSubRow: boolean) => {
            const isGroup = row.rowType === PortfolioRowType.GROUP;
            const isContextOnly = isContextOnlyGroupRow(row);
            // Both counts are read straight off the row: N is the contacts listed under it once the
            // filter has been applied, M what the group would list unfiltered. Equal counts say
            // nothing the listing does not already show, so the line is left out — including the
            // group that lists nothing and has nothing to list.
            const listedMembers = row.memberCount ?? 0;
            const allMembers = row.totalMemberCount ?? 0;
            const showMembersMatched = isGroup && listedMembers < allMembers;
            const officialName =
                !isGroup && row.officialName && row.officialName !== row.name ? row.officialName : null;
            // `<label>` chunks in the translation keys carry the prefix ("Official name:",
            // "Client since:") — rendered in the muted secondary text colour so the value next to it
            // reads as the primary content on the tooltip's light background.
            const secondaryLabel = (chunks: React.ReactNode) => <span className="text-fg-secondary">{chunks}</span>;
            // Client since is suppressed on group rows: the BE aggregates an earliest-member
            // value for sort-key continuity but it has no client meaning at the group level.
            const showClientSince = !isGroup && !!row.clientSince;
            // RP/RM have their own column, so this tooltip is down to the official name and the
            // client-since date — both suppressed on group rows, which therefore have nothing to
            // show and get no icon from it (a context-only group gets its own, below). A party row
            // keeps one either way: `clientSince` falls back to the party's creation date
            // server-side, so it is only ever absent on a group.
            const hasTooltipContent = !!officialName || showClientSince;
            const hasSegmentOrServiceLevel = row.segmentTypeCode != null || row.serviceLevelTypeCode != null;
            const tooltipTitle = (
                <div className="flex flex-col gap-0.5">
                    {officialName ? (
                        <div>
                            <FormattedMessage
                                id="portfolio.row.official-name-line"
                                values={{ name: officialName, label: secondaryLabel }}
                            />
                        </div>
                    ) : null}
                    {showClientSince ? (
                        <div>
                            <FormattedMessage
                                id="portfolio.row.client-since-line"
                                values={{ date: formatDate(row.clientSince), label: secondaryLabel }}
                            />
                        </div>
                    ) : null}
                </div>
            );
            return (
                <div className={cn('flex flex-col gap-1 [word-break:break-word]', isSubRow && 'ml-8')}>
                    <div className="flex items-center gap-1">
                        {isGroup ? (
                            <RouterLink
                                to={`/group/${row.id}`}
                                // The table colours `.entity-link` with an unlayered rule, so the grey has
                                // to be marked important to win against it.
                                className={cn(
                                    'entity-link text-fg hover:text-link font-semibold whitespace-normal no-underline hover:underline',
                                    isContextOnly ? 'text-grey-600!' : undefined
                                )}
                            >
                                {row.name}
                                <GroupIcon size="small" className="ml-1" />
                            </RouterLink>
                        ) : (
                            // Contact rows render through <PartyName> — the same component the global
                            // contacts list uses — so the name link, party number and the auto-rendered
                            // persona-non-grata chip all stay consistent across both views.
                            <PartyName
                                party={{
                                    id: row.id,
                                    name: row.name,
                                    firstName: row.firstName,
                                    lastName: row.lastName,
                                    partyType: row.partyType,
                                    partyNumber: row.partyNumber,
                                    clientSequence: row.clientSequence,
                                    isPersonaNonGrata: row.isPersonaNonGrata ?? undefined,
                                }}
                                isLink
                                plainLink
                                variant="list"
                                showPartyNumber
                                className="font-semibold break-words"
                            />
                        )}
                        {hasTooltipContent ? (
                            <Tooltip content={tooltipTitle}>
                                {/* SVG icons aren't natively focusable — wrap in a tabbable span so the
                                 *  tooltip is reachable via keyboard, not just mouse hover. */}
                                <span
                                    role="img"
                                    tabIndex={0}
                                    className="inline-flex items-center"
                                    aria-label={t('portfolio.row.info-tooltip-aria')}
                                >
                                    <InfoOutlinedIcon className="text-grey-600 h-[1.3rem]" />
                                </span>
                            </Tooltip>
                        ) : null}
                        {isContextOnly ? (
                            <Tooltip content={<FormattedMessage id="portfolio.row.context-only-tooltip" />}>
                                {/* Same tabbable wrapper as the icon above, for the same reason: the
                                 *  glyph is not focusable, so without it the explanation of why the
                                 *  row is grey would be mouse-only. */}
                                <span
                                    role="img"
                                    tabIndex={0}
                                    className="inline-flex items-center"
                                    aria-label={t('portfolio.row.context-only-aria')}
                                >
                                    <InfoOutlinedIcon className="text-grey-600 h-[1.3rem]" />
                                </span>
                            </Tooltip>
                        ) : null}
                    </div>
                    {isGroup && (
                        <div className={cn('flex gap-1', CHIP_ROW_CLASS)}>
                            {hasSegmentOrServiceLevel && (
                                <div className={cn('flex gap-1', CHIP_ROW_CLASS)}>
                                    {row.segmentTypeCode != null && (
                                        <SegmentChip segmentTypeCode={row.segmentTypeCode} />
                                    )}
                                    {row.serviceLevelTypeCode != null && (
                                        <span className={SERVICE_LEVEL_CLASS}>
                                            {getClassifierText(
                                                classifiers['SERVICE_LEVEL_TYPE'],
                                                row.serviceLevelTypeCode
                                            )}
                                        </span>
                                    )}
                                </div>
                            )}
                            {hasSegmentOrServiceLevel && row.groupPartnershipTypeCode != null && (
                                <Separator orientation="vertical" />
                            )}
                            {row.groupPartnershipTypeCode != null && (
                                <span className="text-caption text-fg-secondary new:whitespace-nowrap flex items-center">
                                    {getClassifierText(
                                        classifiers['GROUP_PARTNERSHIP_TYPE'],
                                        row.groupPartnershipTypeCode
                                    )}
                                </span>
                            )}
                        </div>
                    )}
                    {showMembersMatched ? (
                        <div className="text-fg-secondary text-xs">
                            <FormattedMessage
                                id="portfolio.row.members-matched-line"
                                values={{ matched: listedMembers, total: allMembers }}
                            />
                        </div>
                    ) : null}
                    {!isGroup && (
                        <div className={cn('flex items-center gap-1', CHIP_ROW_CLASS)}>
                            {isNonVerifiedIconEnabled &&
                                row.relationshipTypeCode === RelationshipTypeCode.CLIENT &&
                                row.complianceVerificationStatusTypeCode === VerificationStatusType.NOT_VERIFIED && (
                                    <Tooltip content={t('compliance.not-verified')}>
                                        <div>
                                            <Chip
                                                size="small"
                                                label={<ExclamationMarkIcon />}
                                                type={'partyType'}
                                                isError
                                            />
                                        </div>
                                    </Tooltip>
                                )}
                            {row.isTimecardAddingAutoBlocked ? (
                                <Tooltip content={t('dashboard.tooltips.timecard-blocked-short')}>
                                    <div>
                                        <Chip size="small" label={<BlockIcon />} type={'partyType'} isError />
                                    </div>
                                </Tooltip>
                            ) : null}
                            {row.partyType === PartyType.COMPANY ? (
                                <Tooltip content={t('contacts.types.company')}>
                                    <div>
                                        <Chip size="small" label={<BusinessCenter />} type={'partyType'} />
                                    </div>
                                </Tooltip>
                            ) : row.partyType === PartyType.PERSON ? (
                                <Tooltip content={t('contacts.types.person')}>
                                    <div>
                                        <Chip size="small" label={<Person />} type={'partyType'} />
                                    </div>
                                </Tooltip>
                            ) : null}
                            {row.relationshipTypeCode === RelationshipTypeCode.CLIENT ? (
                                <Tooltip content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'CLIENT')}>
                                    <div>
                                        <Chip size="small" label={<AttachMoney />} type={'partyType'} />
                                    </div>
                                </Tooltip>
                            ) : row.relationshipTypeCode === RelationshipTypeCode.LEAD ? (
                                <Tooltip content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'LEAD')}>
                                    <div>
                                        <Chip size="small" label={<ArrowOutwardRoundedIcon />} type={'partyType'} />
                                    </div>
                                </Tooltip>
                            ) : null}
                            {row.segmentTypeCode != null && <SegmentChip segmentTypeCode={row.segmentTypeCode} />}
                            {row.serviceLevelTypeCode != null && (
                                <span className={SERVICE_LEVEL_CLASS}>
                                    {getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], row.serviceLevelTypeCode)}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            );
        },
        [classifiers, isNonVerifiedIconEnabled]
    );

    // Per-cell renderers, defined as callbacks so the column arrays can stay declarative. Several
    // of them pack two stacked lines: a primary value above, a muted/highlighted secondary below.

    // "Last BD effort" column. Renders as a countdown (today / X days ago / X months ago /
    // Never) — same convention as the Contacts global list, so calculateTimeSince() is the single
    // source of truth. We pass 'CLIENT' as the contactType arg so the shared util's CLIENT/LEAD-only
    // highlighting (red past 3 months) fires for every row in My Portfolio, including group rows
    // whose relationshipTypeCode is null. Every row this list renders is a client or lead (or a
    // group holding them) whatever the active scope resolves to, so the per-row relationship type is
    // not what should gate the highlight. A context-only group row is never flagged: its grey mutes a
    // colour, and the flag is also an icon and a screen-reader word.
    const renderLastBdEffortCell = useCallback(
        (row: PortfolioListItem) => {
            const { text, shouldHighlight } = calculateTimeSince(row.lastBdEffort, 'CLIENT', dateFormat);
            return <TimeSinceCell text={text} overdue={shouldHighlight && !isContextOnlyGroupRow(row)} />;
        },
        [dateFormat]
    );

    // The firm's "Last {type}" column: the same countdown as Last BD effort, flagged by the portfolio's
    // "needs attention" rule for the type rather than the 3-month one (null as the contactType switches that off).
    // A context-only group row is never flagged, as above.
    const renderLastHcsMeetingCell = useCallback(
        (row: PortfolioListItem) => (
            <TimeSinceCell
                text={calculateTimeSince(row.lastHCSBdEffort, null, dateFormat).text}
                overdue={
                    !isContextOnlyGroupRow(row) &&
                    isLastEffortStale(lastEffortType, row.segmentTypeCode, row.lastHCSBdEffort)
                }
            />
        ),
        [dateFormat, lastEffortType]
    );

    // Relationship owners — the same two-line "RP: … / RM: …" block the global Contacts and Groups
    // lists render, through the same component, so the three lists cannot drift apart. Group rows
    // carry the group's own RP/RM (from party_group_employee); party rows carry the party's.
    const renderRelationshipOwnersCell = useCallback(
        (row: PortfolioListItem) => (
            <RelationshipPartnersAndManagers
                relationshipPartners={(row.partners ?? []).map(({ employee }) => employee)}
                relationshipManagers={(row.managers ?? []).map(({ employee }) => employee)}
            />
        ),
        []
    );

    const renderPrimaryContactCell = useCallback((row: PortfolioListItem) => {
        const contacts = row.primaryContacts ?? [];
        if (contacts.length === 0) return <span>-</span>;
        return (
            <div className="flex flex-col">
                {contacts.map((contact) => (
                    <RecordLink
                        className="entity-link text-fg hover:text-link [word-break:break-word] whitespace-normal no-underline hover:underline"
                        key={contact.id}
                        to={`/contact/${contact.id}`}
                        label={contact.name}
                    />
                ))}
            </div>
        );
    }, []);

    const renderActiveMattersCell = useCallback(
        (row: PortfolioListItem) => (
            <div className="flex flex-col">
                <span>{row.activeMattersCount ?? 0}</span>
                {row.latestTimecardDate ? (
                    <span className="text-caption text-fg-secondary">
                        <FormattedMessage
                            id="portfolio.columns.last-tc-line"
                            values={{ date: formatDate(row.latestTimecardDate) }}
                        />
                    </span>
                ) : null}
            </div>
        ),
        []
    );

    // Label for the group-row ownership line below, following the active scope. Null in firm scope,
    // where no subset is owned — the backend sums that slice over an empty owner set and returns 0,
    // so the line would read "0" for every group. Memoized: renderYtdRevenueCell closes over it.
    const ownershipLine = useMemo(() => buildOwnershipLineMessage(scope), [scope]);

    // YoY indicator next to YTD Revenue. Suppressed only when last-year-YTD is missing or
    // exactly zero — a percentage off zero is undefined and would render as Infinity / 0%
    // depending on the path. A negative prior baseline (e.g. credit notes outweighing
    // billings last year) is uncommon but real, and a turnaround from a loss year is exactly
    // the sort of signal we want to surface, so we keep prior < 0 in scope. Color follows
    // sign of delta: brand green / up for >= 0, error red / down for < 0.
    //
    // Group rows show a second line with "<owner> ownership: {amount}" — the active scope's RP/RM
    // slice of the group. revenueYtd on group rows is the full-group total (across all members
    // regardless of ownership), so the YoY arrow reflects the group as a whole.
    const renderYtdRevenueCell = useCallback(
        (row: PortfolioListItem) => {
            const current = row.revenueYtd ?? 0;
            const prior = row.revenueYtdM1 ?? 0;
            const showDelta = prior !== 0;
            const delta = showDelta ? Math.round(((current - prior) / Math.abs(prior)) * 100) : 0;
            const isPositive = delta >= 0;
            const showOwnershipLine = row.rowType === PortfolioRowType.GROUP && row.myOwnershipRevenueYtd != null;
            return (
                <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                        <MoneyDisplay amount={current} decimalScale={0} />
                        {showDelta ? (
                            <div className={cn('flex items-center', isPositive ? 'text-primary' : 'text-error')}>
                                {isPositive ? (
                                    <ArrowUpwardRoundedIcon className="text-[1rem]" />
                                ) : (
                                    <ArrowDownwardRoundedIcon className="text-[1rem]" />
                                )}
                                <span className="text-caption">{`${isPositive ? '+' : ''}${delta}%`}</span>
                            </div>
                        ) : null}
                    </div>
                    {showOwnershipLine && ownershipLine ? (
                        <span className="text-caption text-fg-secondary">
                            <FormattedMessage
                                id={ownershipLine.id}
                                values={{
                                    ...ownershipLine.values,
                                    amount: (
                                        <MoneyDisplay
                                            key="amount"
                                            amount={row.myOwnershipRevenueYtd ?? 0}
                                            decimalScale={0}
                                        />
                                    ),
                                }}
                            />
                        </span>
                    ) : null}
                </div>
            );
        },
        [ownershipLine]
    );

    const renderOutstandingOverdueCell = useCallback(
        (row: PortfolioListItem) => (
            <div className="flex flex-col">
                <MoneyDisplay amount={row.outstandingBalance ?? 0} decimalScale={0} />
                {(row.overdueAmount ?? 0) > 0 ? (
                    <span className="text-caption text-error">
                        <FormattedMessage
                            id="portfolio.columns.overdue-line"
                            values={{
                                amount: <MoneyDisplay key="amount" amount={row.overdueAmount ?? 0} decimalScale={0} />,
                            }}
                        />
                    </span>
                ) : null}
            </div>
        ),
        []
    );

    const renderActionsCell = useCallback(
        (row: PortfolioListItem, isSubRow: boolean) => (
            <KebabMenu
                menuItems={buildRowActions(row.id)}
                // On a member row the trigger takes the nested table's surface instead of its own opaque white.
                className={cn('h-full', isSubRow && 'bg-grey-50')}
                icon={<MoreVertIcon fontSize="small" />}
            />
        ),
        [buildRowActions]
    );

    // Weights are the proportions this table is laid out on; the managed layout resolves them back
    // to the same ratios. With the last-effort-type column hidden its weight simply leaves the pool and the
    // remaining columns widen proportionally.
    const columns = useMemo<DataTableColumnDef<PortfolioListItem>[]>(
        () => [
            {
                header: t('portfolio.columns.groups-clients-leads'),
                id: 'name',
                accessorKey: 'name',
                meta: {
                    sortAccessor: 'name',
                    widthWeight: 2.0,
                    minPx: 210,
                },
                cell: ({ row }) => renderNameCell(row.original, false),
            },
            {
                header: t('contacts.relationship-owners'),
                id: 'relationshipOwners',
                enableSorting: false,
                meta: {
                    widthWeight: 1.4,
                    minPx: 160,
                },
                cell: ({ row }) => renderRelationshipOwnersCell(row.original),
            },
            {
                header: t('portfolio.columns.last-bd-effort'),
                id: 'lastBdEffort',
                accessorKey: 'lastBdEffort',
                meta: {
                    sortAccessor: 'lastBdEffort',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.0,
                    // The new design's overdue cell holds an icon beside "12 months ago", on one line.
                    minPx: isNewDesign ? 134 : 120,
                },
                cell: ({ row }) => greyWhenContextOnly(row.original, renderLastBdEffortCell(row.original)),
            },
            // Only firms that configure a last-effort type get this column.
            ...(hasLastEffortType
                ? ([
                      {
                          header: lastEffortTypeLabel,
                          id: 'lastHCSMeeting',
                          accessorKey: 'lastHCSBdEffort',
                          meta: {
                              sortAccessor: 'lastHCSBdEffort',
                              nullHandling: 'nullsLast',
                              widthWeight: 1.0,
                              // The same overdue cell as Last BD effort, so the same room for its icon.
                              minPx: isNewDesign ? 134 : 120,
                          },
                          cell: ({ row }) => greyWhenContextOnly(row.original, renderLastHcsMeetingCell(row.original)),
                      },
                  ] satisfies DataTableColumnDef<PortfolioListItem>[])
                : []),
            {
                header: t('portfolio.columns.primary-contact-persons'),
                id: 'primaryContact',
                enableSorting: false,
                meta: {
                    widthWeight: 1.5,
                    minPx: 140,
                },
                cell: ({ row }) => renderPrimaryContactCell(row.original),
            },
            {
                header: t('portfolio.columns.active-matters'),
                id: 'activeMatters',
                accessorKey: 'activeMattersCount',
                meta: {
                    sortAccessor: 'activeMattersCount',
                    nullHandling: 'nullsLast',
                    widthWeight: 0.9,
                    minPx: 100,
                },
                cell: ({ row }) => greyWhenContextOnly(row.original, renderActiveMattersCell(row.original)),
            },
            {
                header: t('portfolio.columns.revenue-ytd'),
                id: 'revenueYtd',
                accessorKey: 'revenueYtd',
                meta: {
                    sortAccessor: 'revenueYtd',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.2,
                    minPx: 130,
                },
                cell: ({ row }) => greyWhenContextOnly(row.original, renderYtdRevenueCell(row.original)),
            },
            {
                header: () => (
                    <TooltipHeader
                        label="portfolio.columns.outstanding-overdue"
                        tooltip="portfolio.columns.outstanding-overdue-tooltip"
                    />
                ),
                id: 'outstandingOverdue',
                accessorKey: 'outstandingBalance',
                meta: {
                    sortAccessor: 'outstandingBalance',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.3,
                    minPx: 150,
                    // The node header leaves the column picker without a readable label.
                    pickerLabel: t('portfolio.columns.outstanding-overdue'),
                },
                cell: ({ row }) => greyWhenContextOnly(row.original, renderOutstandingOverdueCell(row.original)),
            },
            {
                header: '',
                id: 'actions',
                enableSorting: false,
                meta: {
                    // Contact rows render a kebab, group rows a 32px chevron, inside the managed cell's
                    // 32px horizontal padding — 76, the same width every other kebab column in the app uses.
                    fixedPx: 76,
                },
                cell: ({ row }) => {
                    if (row.original.rowType === PortfolioRowType.GROUP) {
                        const hasMembers = (row.original.members?.length ?? 0) > 0;
                        if (!hasMembers) return null;
                        return (
                            <TableRowCallForActionButton
                                onClick={row.getToggleExpandedHandler()}
                                style={{ cursor: 'pointer' }}
                                aria-label={t(row.getIsExpanded() ? 'collapse-row' : 'expand-row')}
                                shape="rounded"
                            >
                                <ExpandMoreRoundedIcon
                                    className={cn(
                                        'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                        row.getIsExpanded() && '-scale-y-100'
                                    )}
                                />
                            </TableRowCallForActionButton>
                        );
                    }
                    return renderActionsCell(row.original, false);
                },
            },
        ],
        [
            hasLastEffortType,
            lastEffortTypeLabel,
            renderNameCell,
            renderRelationshipOwnersCell,
            renderLastBdEffortCell,
            renderLastHcsMeetingCell,
            renderPrimaryContactCell,
            renderActiveMattersCell,
            renderYtdRevenueCell,
            renderOutstandingOverdueCell,
            renderActionsCell,
            isNewDesign,
        ]
    );

    // The nested columns mirror the parent table's RESOLVED widths so the per-row cells line up
    // vertically with the parent row above: each width reads the parent column's `--utw-w-<id>`
    // variable (written on the table's scroll container by the managed layout, so it is in scope
    // here). The nested table sits inside a cell with a 16px inset on each side, so 32px has to be
    // given back somewhere; the two columns that give it up are the name column and the last MONEY
    // column, which leaves every other boundary aligned with the parent's.
    //
    // Two things the arithmetic has to respect:
    //   - It must NOT come off `actions`. That column is `fixedPx` in the parent table, i.e. sized to
    //     its kebab with no slack, so shaving it pushes the button past the cell edge — where it
    //     loses its right margin and paints over the row's right accent bar (an inset shadow renders
    //     beneath child content). Only a flex column can absorb the inset.
    //   - The give-back is 34px, not 32: the nested table is wrapped in a bordered container, and its
    //     1px left + right borders come out of the same budget. Take only 32 and the last cell's
    //     2px accent bar lands under that border and is clipped away by its `overflow: hidden`.
    //
    // The fallbacks are the parent table's weights expressed as percentages, for the case where its
    // width variable is not set yet.
    const nestedColumns = useMemo<NestedColumn<PortfolioListItem>[]>(
        () => [
            {
                id: 'name',
                header: t('portfolio.columns.clients-leads'),
                width: `calc(var(${columnWidthVar('name')}, 19%) - 16px)`,
                sortAccessor: (row) => row.name ?? null,
                Cell: (row) => renderNameCell(row, true),
            },
            {
                id: 'relationshipOwners',
                header: t('contacts.relationship-owners'),
                width: `var(${columnWidthVar('relationshipOwners')}, 13%)`,
                Cell: renderRelationshipOwnersCell,
            },
            {
                id: 'lastBdEffort',
                header: t('portfolio.columns.last-bd-effort'),
                width: `var(${columnWidthVar('lastBdEffort')}, 9%)`,
                // BE returns lastBdEffort as ISO 'yyyy-MM-dd', which sorts correctly under string
                // comparison so it shares the same code path as the name sort above.
                sortAccessor: (row) => row.lastBdEffort ?? null,
                Cell: renderLastBdEffortCell,
            },
            ...(hasLastEffortType
                ? ([
                      {
                          id: 'lastHCSMeeting',
                          header: lastEffortTypeLabel,
                          width: `var(${columnWidthVar('lastHCSMeeting')}, 9%)`,
                          sortAccessor: (row) => row.lastHCSBdEffort ?? null,
                          Cell: renderLastHcsMeetingCell,
                      },
                  ] satisfies NestedColumn<PortfolioListItem>[])
                : []),
            {
                id: 'primaryContact',
                header: t('portfolio.columns.primary-contact-persons'),
                width: `var(${columnWidthVar('primaryContact')}, 14%)`,
                Cell: renderPrimaryContactCell,
            },
            {
                id: 'activeMatters',
                header: t('portfolio.columns.active-matters'),
                width: `var(${columnWidthVar('activeMatters')}, 8%)`,
                sortAccessor: (row) => row.activeMattersCount ?? null,
                Cell: renderActiveMattersCell,
            },
            {
                id: 'revenueYtd',
                header: t('portfolio.columns.revenue-ytd'),
                width: `var(${columnWidthVar('revenueYtd')}, 11%)`,
                sortAccessor: (row) => row.revenueYtd ?? null,
                Cell: renderYtdRevenueCell,
            },
            {
                id: 'outstandingOverdue',
                header: (
                    <TooltipHeader
                        label="portfolio.columns.outstanding-overdue"
                        tooltip="portfolio.columns.outstanding-overdue-tooltip"
                    />
                ),
                width: `calc(var(${columnWidthVar('outstandingOverdue')}, 12%) - 18px)`,
                sortAccessor: (row) => row.outstandingBalance ?? null,
                Cell: renderOutstandingOverdueCell,
            },
            {
                id: 'actions',
                header: '',
                width: `var(${columnWidthVar('actions')}, 5%)`,
                Cell: (row) => renderActionsCell(row, true),
            },
        ],
        [
            hasLastEffortType,
            lastEffortTypeLabel,
            renderNameCell,
            renderRelationshipOwnersCell,
            renderLastBdEffortCell,
            renderLastHcsMeetingCell,
            renderPrimaryContactCell,
            renderActiveMattersCell,
            renderYtdRevenueCell,
            renderOutstandingOverdueCell,
            renderActionsCell,
        ]
    );

    // Orange "my clients" side-border. Only meaningful in the GROUP view — the CONTACTS view
    // is filtered to the user's parties at source so every row would carry the border
    // (redundant and noisy, suppressed entirely).
    //
    // Visible row (Tier 1 / nested member rows): bordered iff the BE-computed `isMine` flag is
    // true — i.e. the user is RP/RM of the entity itself (party_employee for contacts,
    // party_group_employee for groups).
    //
    // Expanded wrapper cell (around a group's nested table): bordered iff every MEMBER is
    // mine — the bracket then reads as "the whole expanded block is yours". A mixed group
    // (any non-mine member is surfaced for the full picture) gets a
    // bordered header row but an un-bordered expanded block.
    const myClientsEdgeColor = 'var(--secondary)';
    const getRowEdgeColor = useCallback(
        (row: PortfolioListItem) => (viewMode === PortfolioViewMode.GROUP && row.isMine ? myClientsEdgeColor : null),
        [myClientsEdgeColor, viewMode]
    );
    const getExpandedRowEdgeColor = useCallback(
        (row: PortfolioListItem) => {
            if (viewMode !== PortfolioViewMode.GROUP || row.rowType !== PortfolioRowType.GROUP) return null;
            const members = row.members ?? [];
            return members.length > 0 && members.every((member) => member.isMine) ? myClientsEdgeColor : null;
        },
        [myClientsEdgeColor, viewMode]
    );

    const renderExpandedContent = useCallback(
        (row: PortfolioListItem): JSX.Element => (
            // Defensive: only group rows have members and only group rows expose the expand
            // toggle, but renderExpandedContent is wired at the table level for all rows so
            // this guard makes the contract explicit.
            <NestedTable<PortfolioListItem>
                data={row.rowType === PortfolioRowType.GROUP && row.members?.length ? row.members : []}
                columns={nestedColumns}
                getRowKey={(member) => member.id}
                rowEdgeColor={getRowEdgeColor}
            />
        ),
        [nestedColumns, getRowEdgeColor]
    );

    // Null in firm scope, which is what drops the quick filter from the config below. The tooltip is
    // a message object, so it must be memoized: it feeds filterConfig, and a fresh identity there
    // trips useListTableFilter's filterConfig-changed effect into a refetch on every render.
    const ownershipFilterLabel = buildOwnershipFilterLabel(scope);
    const ownershipFilterTooltip = useMemo(() => buildOwnershipFilterTooltip(scope), [scope]);

    // The table's remount key is the scope alone (`portfolioScopeKey`), so changing people within
    // the "People" scope does not remount it — a remount would tear down the open colleague
    // multiselect. Scope and userIds live in the URL (preservedParamKeys) and
    // useListTableFilter's searchParams-change effect refetches when they change, picking up the
    // new userIds via the fetchDataWithViewMode closure.
    const filterConfig: FilterConfig = useMemo(
        () => ({
            // Quick filters. `selector` is a dummy (the operator carries all the
            // semantics) — the BE-registered RSQL operator name in `comperator` is what drives
            // the predicate. Defaults (active + PNG selected on first visit) are seeded via
            // INITIAL_SEARCH_PARAMS, not `defaultSelected: true` — see the constant's comment.
            // My-ownership is groups-only — in contacts view it'd be a no-op (contacts view is
            // already RP/RM-scoped at source) so we omit it from the config; the `key={viewMode}`
            // on the table forces a remount when the user switches modes, so filter state never
            // leaks between modes. It is also omitted in FIRM scope, where the backend drops the
            // filter (nothing is "owned" firm-wide) — rendering it there would be a dead control.
            quickFilters: [
                {
                    selector: 'id',
                    comperator: '=isportfolioactiveclient=',
                    filters: [
                        {
                            tooltip: 'portfolio.filter.active-clients-only-tooltip',
                            label: t('portfolio.filter.active-clients-only'),
                            argument: String(ACTIVE_CLIENT_LOOKBACK_MONTHS),
                        },
                    ],
                },
                {
                    selector: 'id',
                    comperator: '=excludepersonanongrata=',
                    filters: [
                        {
                            tooltip: 'portfolio.filter.hide-persona-non-grata-tooltip',
                            label: t('portfolio.filter.hide-persona-non-grata'),
                            argument: 'true',
                        },
                    ],
                },
                ...(viewMode === PortfolioViewMode.GROUP && ownershipFilterLabel
                    ? [
                          {
                              selector: 'id',
                              comperator: '=isportfoliomyownership=',
                              filters: [
                                  {
                                      tooltip: ownershipFilterTooltip?.id,
                                      tooltipValues: ownershipFilterTooltip?.values,
                                      label: ownershipFilterLabel,
                                      argument: 'true',
                                  },
                              ],
                          },
                      ]
                    : []),
            ],
            // Filter panel: a trimmed version of the Contacts filter set. Applies on top of
            // whichever scope is active.
            filters: buildPortfolioMainFilters(
                classifiers,
                hasLastEffortType,
                lastEffortTypeName,
                scope.scope,
                hasSeveralActiveOffices
            ),
            searchBarSelectors: [{ selector: 'name' }],
        }),
        [
            viewMode,
            classifiers,
            hasLastEffortType,
            lastEffortTypeName,
            scope.scope,
            ownershipFilterLabel,
            ownershipFilterTooltip,
            hasSeveralActiveOffices,
        ]
    );

    // The /v1/portfolio/parties endpoint already scopes results to the user's RP/RM
    // relationships server-side, so the previous "My clients" quick filter was always-on
    // and redundant. Replaced here with a passive legend rendered in the same row position
    // the checkbox occupied, with the bright orange (`secondary` — the same fill used
    // for checkbox/toggle "checked" states elsewhere in the app) so it stays visually anchored
    // to the data it labels. It lives in <PortfolioOwnershipLegend> above so the early return
    // for `isLoadingClassifiers` short-circuits before its subtree renders.

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    // Scope-aware legend label; null in firm scope, where the orange border and its label drop out
    // (the grey-row key below can still bring the block back on its own).
    const legendLabel = buildPortfolioLegendLabel(scope);
    // Page-scoped, and deliberately not a backend aggregate: the key is only worth showing while a
    // grey row is actually on screen, and `data` holds exactly the rows fetched so far.
    const hasContextRows = (data?.content ?? []).some(isContextOnlyGroupRow);

    const viewModeToggle = (
        <ViewSwitch value={viewMode} onChange={setViewMode} aria-label={t('portfolio.filter.view')}>
            <Tooltip content={<FormattedMessage id="portfolio.filter.group-view-tooltip" />}>
                <ViewSwitchItem value={PortfolioViewMode.GROUP}>
                    <ViewCozyIcon className="inline-block size-5 shrink-0" />
                    <FormattedMessage id="portfolio.filter.group-view" />
                </ViewSwitchItem>
            </Tooltip>
            <Tooltip content={<FormattedMessage id="portfolio.filter.contacts-view-tooltip" />}>
                <ViewSwitchItem value={PortfolioViewMode.CONTACTS}>
                    <FormatListBulletedIcon fontSize="small" />
                    <FormattedMessage id="portfolio.filter.contacts-view" />
                </ViewSwitchItem>
            </Tooltip>
        </ViewSwitch>
    );

    return (
        // Keying by viewMode forces a clean remount when the user toggles between Groups and
        // Contacts. ListTableWithFilters' initial-fetch is ref-guarded inside
        // useListTableFilter, so a new fetchData closure (with the updated viewMode in
        // extraParams) wouldn't otherwise trigger a refetch — the data would stay on the
        // previously fetched mode. Search/sort state lives in the URL via react-router, so it
        // survives the remount, as does the view itself.
        <ListTableWithFilters<PortfolioListItem>
            key={viewMode}
            getRowHref={getPortfolioRowHref}
            columnConfigId="my-portfolio"
            columns={columns}
            renderExpandedContent={renderExpandedContent}
            rowEdgeColor={getRowEdgeColor}
            expandedRowEdgeColor={getExpandedRowEdgeColor}
            data={data}
            isLoading={isLoadingClassifiers || isFetching}
            pluralTextId="portfolio.clients-plural"
            singularTextId="portfolio.clients-singular"
            // Groups view's row count would be misleading (1 row per qualifying group rolls
            // multiple owned contacts into one, and a group can also surface a row without
            // contributing any owned contact). Suppress the counter in that mode; Contacts
            // view is a flat 1-row-per-contact list, so the count is meaningful there.
            hideElementCount={viewMode === PortfolioViewMode.GROUP}
            noEntriesId="portfolio.no-entries"
            noEntriesIcon={FolderSharedOutlinedIcon}
            fetchData={fetchDataWithViewMode}
            filterConfig={filterConfig}
            rowsPerPage={50}
            quickFilterLeading={
                legendLabel || hasContextRows ? (
                    <PortfolioOwnershipLegend label={legendLabel} showContextRowKey={hasContextRows} />
                ) : undefined
            }
            quickFilterTrailing={
                <>
                    {canUseScopeSelector ? <PortfolioScopeSelector value={scope} onChange={onScopeChange} /> : null}
                    {viewModeToggle}
                </>
            }
            initialSearchParams={INITIAL_SEARCH_PARAMS}
            // Scope is saved by filter persistence too; the view deliberately is not.
            preservedParamKeys={PORTFOLIO_PRESERVED_PARAM_KEYS}
        />
    );
};

export default MyPortfolioTable;
